import React, { ChangeEvent, useEffect, useState } from "react";
import Toggle from "../../components/Toggle";
import { ReactComponent as CancelIcon } from "../../../assets/icons/backspace-20-regular.svg?react";
import { ReactComponent as UndoIcon } from "../../../assets/icons/undo-clear.svg?react";
import { ReactComponent as OkayIcon } from "../../../assets/icons/checkbox-checked-regular.svg?react";
import { ReactComponent as ExperimentIcon } from "../../../assets/icons/experiment-icon.svg?react";
import { Preferences, defaultPrefs } from "../../../interfaces/Preferences";
import AbstractButton from "../../components/AbstractButton";
import ForegroundColorSet from "../../../utilities/enums/ForegroundColorSet";
import BackgroundColorSet from "../../../utilities/enums/BackgroundColorSet";
import { useDispatch, useSelector } from "react-redux";
import { clearStockSlice } from "../../../store/slices/stockcheckslice";
import { setRecentParts } from "../../../store/slices/systemSlice";
import { Slide, toast, ToastContainer } from "react-toastify";
import { RootState } from "../../../store/store";
import {
  AuthState,
  PartsAppFeature,
  setAuth,
} from "../../../store/slices/authSlice";
import { apiFetch } from "../../../utilities/AuthenticatedFetch";
import PasswordConfirmDialogue from "./PasswordConfirmDialogue";

interface SettingsDialogueProps {
  visibilityClassName: string;
  currentPreferences: Preferences;
  onCancelCallback(): void;
  onOkayCallback(prefs: Preferences, successMessage: string): void;
}

const SettingsDialogue = (props: SettingsDialogueProps) => {
  const dispatch = useDispatch();
  const [preferences, setPreferences] = useState<Preferences>(defaultPrefs);
  const [isClearing, setIsClearing] = useState<boolean>(false);
  const [isEnablingFeatures, setIsEnablingFeatures] = useState<boolean>(false);
  const [featuresAvailable, setFeaturesAvailable] = useState<boolean>(false);
  const [showPasswordConfirm, setShowPasswordConfirm] =
    useState<boolean>(false);

  const ENABLE_FEATURE_ENDPOINT = "/partsapp/auth/enableEAFeatures";
  const HASHED_PW = "0atxq81";

  const authState: AuthState = useSelector(
    (state: RootState) => state.root.auth
  );

  const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

  useEffect(() => {
    setFeaturesAvailable(!authState.features.every((f) => f.created));
  }, [props.currentPreferences]);

  useEffect(() => {
    setPreferences(props.currentPreferences);
  }, [props.currentPreferences.preferExternalScanner]);

  const handleDeviceCameraToggle = (event: ChangeEvent<HTMLInputElement>) => {
    setPreferences({
      ...preferences,
      preferExternalScanner: event.target.checked,
    });
  };

  const handleResetDataClick = async () => {
    setIsClearing(true);
    await sleep(500);
    dispatch(clearStockSlice());
    dispatch(setRecentParts([]));
    toast.success("Saved Data Cleared!", {
      position: "bottom-center",
      autoClose: 2000,
      theme: "colored",
      transition: Slide,
    });
    setIsClearing(false);
  };

  const handleEaFeaturesClick = async () => {
    setShowPasswordConfirm(true);
  };

  const handleIncorrectPassword = async () => {
    setShowPasswordConfirm(false);
  };

  const proceedEnablingFeatures = async () => {
    setShowPasswordConfirm(false);
    setIsEnablingFeatures(true);
    await sleep(250);

    const requestOptions: RequestInit = {
      method: "POST",
      headers: { "Content-Type": "application/json" },
    };

    try {
      const response = await apiFetch<PartsAppFeature[]>(
        ENABLE_FEATURE_ENDPOINT,
        requestOptions
      );

      dispatch(setAuth({ ...authState, features: response }));

      toast.success("Development Partner Features Enabled!", {
        position: "bottom-center",
        autoClose: 2000,
        theme: "colored",
        transition: Slide,
      });
    } catch (err) {
      let message: string;
      if (err instanceof Error) {
        message = err.message;
      } else {
        message = String(err);
      }

      toast.error("Failed to Enable Features - " + message, {
        position: "bottom-center",
        autoClose: 2000,
        theme: "colored",
        transition: Slide,
      });

      console.log(err);
    }

    await sleep(250);
    setIsEnablingFeatures(false);
  };

  const handleOkayClick = () => {
    props.onOkayCallback(preferences, "successfully updated preferences");
  };

  return (
    <div
      id="home-header-change-depot-modal"
      className={props.visibilityClassName}
    >
      <PasswordConfirmDialogue
        desiredPassword={HASHED_PW}
        showPasswordDialogue={showPasswordConfirm}
        correctPasswordCallback={proceedEnablingFeatures}
        wrongPasswordCallback={handleIncorrectPassword}
      />
      <span className="modal-header-text"> Settings </span>
      <span className="modal-toggle-container">
        <span className="settings-item-label">
          Default to Camera for Barcodes:
        </span>
        <Toggle
          id="device-camera-toggle"
          className="large-toggle"
          defaultChecked={preferences.preferExternalScanner}
          checked={preferences.preferExternalScanner}
          onChange={handleDeviceCameraToggle}
        />
      </span>
      <span className="modal-toggle-container">
        <span className="settings-item-label">
          Clear All Stored/Saved Data:
        </span>
        <AbstractButton
          id="clear-button"
          specialClass="modal-button settings-action"
          text={null}
          icon={<UndoIcon width="100%" height="100%" />}
          disabled={false}
          isLoading={isClearing}
          activeColor={ForegroundColorSet.Red}
          background={BackgroundColorSet.Black1}
          actionCallback={handleResetDataClick}
        />
      </span>
      <span className="modal-toggle-container">
        <span
          className={
            "settings-item-label" + (featuresAvailable ? "" : " span-disabled")
          }
        >
          Enable Development Partner Features:
        </span>
        <AbstractButton
          id="ea-features-button"
          specialClass="modal-button settings-action"
          text={null}
          icon={<ExperimentIcon width="100%" height="100%" />}
          disabled={!featuresAvailable}
          isLoading={isEnablingFeatures}
          activeColor={ForegroundColorSet.Gold}
          background={BackgroundColorSet.Black1}
          actionCallback={handleEaFeaturesClick}
        />
      </span>
      <span className="modal-button-span">
        <AbstractButton
          id="cancel-button"
          specialClass="modal-button"
          text="Cancel"
          icon={<CancelIcon width="100%" height="100%" />}
          disabled={false}
          activeColor={ForegroundColorSet.Red}
          background={BackgroundColorSet.Black1}
          actionCallback={props.onCancelCallback}
        />
        <AbstractButton
          id="settings-okay-button"
          text="Change"
          specialClass="modal-button"
          icon={<OkayIcon width="100%" height="100%" />}
          disabled={false}
          activeColor={ForegroundColorSet.Green}
          background={BackgroundColorSet.Black1}
          actionCallback={handleOkayClick}
        />
      </span>
      <ToastContainer></ToastContainer>
    </div>
  );
};

export default SettingsDialogue;
